import React, { useState, useEffect } from 'react';
import {
    Avatar,
    Typography,
    Button,
    Form,
    Input,
    Modal,
    message,
    Spin,
    Space,
    Card, App
} from 'antd';
import {
    IdcardOutlined,
    TeamOutlined
} from '@ant-design/icons';
import { userService, authService } from '../services/api';
import {usePageTitle} from "../context/PageTitleContext";

const { Title, Text } = Typography;

const UserProfile = () => {
    const { message } = App.useApp();
    const { setPageTitle, setRightContent } = usePageTitle();

    useEffect(() => {
        // Устанавливаем заголовок для страницы
        setPageTitle('Профиль');

        // Очистка при размонтировании
        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle]);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [profileData, setProfileData] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const currentUser = authService.getCurrentUser();

    useEffect(() => {
        fetchUserProfile();
    }, []);

    const fetchUserProfile = async () => {
        try {
            setLoading(true);
            const userData = await userService.getUserProfile(currentUser.id);
            setProfileData(userData);
        } catch (error) {
            message.error('Не удалось загрузить данные профиля');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const showEditModal = () => {
        form.setFieldsValue({
            first_name: profileData.first_name,
            last_name: profileData.last_name,
            email: profileData.email,
            department: profileData.department || '',
            position: profileData.position || '',
            username: profileData.username
        });
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const response = await userService.updateUserProfile(currentUser.id, values);

            const { success, message: responseMessage } = response || {};

            if (success) {
                setProfileData({
                    ...profileData,
                    ...values
                });
                message.success(responseMessage);
                setIsModalVisible(false);
            } else {
                message.error(responseMessage || 'Ошибка обновления профиля');
            }
        } catch (error) {
            if (error.response && error.response.status === 403) {
                message.error('Доступ запрещен');
            } else {
                message.error(error.message || 'Не удалось обновить профиль');
            }
            console.error('Update profile error:', error);
        } finally {
            setLoading(false);
        }
    };



    const getInitials = (firstName, lastName) => {
        if (!firstName && !lastName) return 'U';
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Не указано';
        return new Date(dateString).toLocaleDateString('ru-RU');
    };

    if (loading && !profileData) {
        return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
    }

    return (
        <>
            {/* Верхний блок с аватаркой и базовой информацией */}
            <Card
                style={{
                    marginBottom: 24
                }}
            >
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Avatar
                        size={80}
                        style={{ backgroundColor: '#1677ff', fontSize: 36, marginRight: 24 }}
                    >
                        {getInitials(profileData?.first_name, profileData?.last_name)}
                    </Avatar>

                    <div>
                        <Title level={3} style={{ marginTop: 0, marginBottom: 8 }}>
                            {profileData?.first_name} {profileData?.last_name}
                        </Title>
                        <Text type="secondary">{profileData?.username}</Text>
                        <div style={{ marginTop: 16 }}>
                            <Button
                                type="primary"
                                onClick={showEditModal}
                            >
                                Редактировать данные
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Нижний блок с детальной информацией */}
            <Card
                title="Личная информация"
            >
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    <div style={{ width: '100%', marginBottom: 24 }}>
                        <Text type="secondary">Email</Text>
                        <div style={{ fontSize: 16, marginTop: 4 }}>
                            {profileData?.email}
                        </div>
                    </div>

                    <div style={{ width: '50%', marginBottom: 24, paddingRight: 12 }}>
                        <Text type="secondary">Отдел</Text>
                        <div style={{ fontSize: 16, marginTop: 4 }}>
                            <TeamOutlined style={{ marginRight: 8 }} />
                            {profileData?.department || 'Не указан'}
                        </div>
                    </div>

                    <div style={{ width: '50%', marginBottom: 24 }}>
                        <Text type="secondary">Должность</Text>
                        <div style={{ fontSize: 16, marginTop: 4 }}>
                            <IdcardOutlined style={{ marginRight: 8 }} />
                            {profileData?.position || 'Не указана'}
                        </div>
                    </div>

                    <div style={{ width: '50%', paddingRight: 12 }}>
                        <Text type="secondary">Дата регистрации</Text>
                        <div style={{ fontSize: 16, marginTop: 4 }}>
                            {formatDate(profileData?.created_at)}
                        </div>
                    </div>

                    <div style={{ width: '50%' }}>
                        <Text type="secondary">Последнее обновление</Text>
                        <div style={{ fontSize: 16, marginTop: 4 }}>
                            {formatDate(profileData?.updated_at)}
                        </div>
                    </div>
                </div>
            </Card>

            <Modal
                title="Редактирование профиля"
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                width={500}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        name="first_name"
                        label="Имя"
                        rules={[{ required: true, message: 'Пожалуйста, введите имя' }]}
                    >
                        <Input placeholder="Имя" />
                    </Form.Item>

                    <Form.Item
                        name="last_name"
                        label="Фамилия"
                        rules={[{ required: true, message: 'Пожалуйста, введите фамилию' }]}
                    >
                        <Input placeholder="Фамилия" />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                            >
                                Сохранить
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default UserProfile;